import { Controller } from '@hotwired/stimulus'
import { get } from '@rails/request.js'

export enum Status {
  loading = 'loading',
  done = 'done',
  error = 'error',
  notReady = 'notReady'
}

type DownloadResponse = { ready: false } | { ready: true; url: string }
type Boxes = { [key in Status]?: HTMLElement }

export const POLL_INTERVAL_MS = 2000
export const MAX_ATTEMPTS = 150
const ELAPSED_TICK_MS = 1000
const RESUMABLE: readonly Status[] = [Status.error, Status.notReady]

export default class extends Controller<HTMLDivElement> {
  static targets = ['loading', 'done', 'error', 'notReady', 'elapsed']

  declare readonly loadingTarget: HTMLElement
  declare readonly doneTarget: HTMLElement
  declare readonly errorTarget: HTMLElement
  declare readonly notReadyTarget: HTMLElement
  declare readonly elapsedTarget: HTMLElement
  declare readonly hasElapsedTarget: boolean

  static values = {
    url: String,
    pollInterval: { type: Number, default: POLL_INTERVAL_MS },
    maxAttempts: { type: Number, default: MAX_ATTEMPTS }
  }

  declare urlValue: string
  declare pollIntervalValue: number
  declare maxAttemptsValue: number

  status = Status.loading
  attempts = 0
  intervalId?: number
  elapsedIntervalId?: number
  elapsedSeconds = 0
  latestRequestId = 0
  boxes: Boxes = {}

  initialize(): void {
    this.boxes = {
      loading: this.loadingTarget,
      done: this.doneTarget,
      error: this.errorTarget,
      notReady: this.notReadyTarget
    }
    this.#toggleByStatus()
  }

  connect(): void {
    this.#startPolling()
  }

  disconnect(): void {
    this.#cancelPolling()
  }

  checkDownload(): void {
    if (this.attempts >= this.maxAttemptsValue) {
      this.#stopPolling(Status.notReady)
      return
    }

    this.attempts += 1
    void this.#pollDownload(++this.latestRequestId)
  }

  retry(): void {
    if (!RESUMABLE.includes(this.status)) return

    this.#cancelPolling()
    this.attempts = 0
    this.elapsedSeconds = 0
    this.status = Status.loading
    this.#toggleByStatus()
    this.checkDownload()
    this.#startPolling()
  }

  #toggleByStatus(): void {
    Object.values(this.boxes).forEach(box => {
      box.classList.toggle('hidden', this.boxes[this.status] !== box)
    })
  }

  #stopPolling(status: Status): void {
    this.status = status
    this.#toggleByStatus()
    this.#cancelPolling()
  }

  // The elapsed clock times the wait, so it lives and dies with the polling that causes it:
  // a settled screen freezes its reading, and `retry` starts counting from zero again.
  #startPolling(): void {
    this.intervalId ??= window.setInterval(() => this.checkDownload(), this.pollIntervalValue)
    this.#startElapsedTimer()
  }

  #cancelPolling(): void {
    this.latestRequestId += 1
    window.clearInterval(this.intervalId)
    this.intervalId = undefined
    this.#stopElapsedTimer()
  }

  #startElapsedTimer(): void {
    if (!this.hasElapsedTarget) return

    this.#renderElapsedTime()
    this.elapsedIntervalId ??= window.setInterval(() => {
      this.elapsedSeconds += 1
      this.#renderElapsedTime()
    }, ELAPSED_TICK_MS)
  }

  #stopElapsedTimer(): void {
    window.clearInterval(this.elapsedIntervalId)
    this.elapsedIntervalId = undefined
  }

  #renderElapsedTime(): void {
    const minutes = Math.floor(this.elapsedSeconds / 60)
    const seconds = String(this.elapsedSeconds % 60).padStart(2, '0')
    this.elapsedTarget.textContent = `Waiting for ${minutes}:${seconds}`
  }

  async #pollDownload(requestId: number): Promise<void> {
    const url = this.urlValue || window.location.href

    try {
      const response = await get(url, { responseKind: 'json' })
      if (!response.ok) throw new Error(`Could not check ${url}`)

      const download = (await response.json) as DownloadResponse
      if (!this.#isCurrent(requestId) || !download.ready) return

      window.location.href = download.url
      this.#stopPolling(Status.done)
    } catch {
      if (this.#isCurrent(requestId)) this.#stopPolling(Status.error)
    }
  }

  #isCurrent(requestId: number): boolean {
    return requestId === this.latestRequestId
  }
}
